import React from 'react';
import { CheckCircleIcon, XIcon, ClockIcon, BookmarkIcon } from './icons/InterfaceIcons';
import { calculateAnalytics } from '../utils/analyticsEngine';

interface PerformanceAnalyticsProps {
    results: {
        correct: number;
        incorrect: number;
        unattempted: number;
        accuracy: number;
        score: number;
        time_taken: number;
        questionTimes?: { [key: string]: number };
        answers?: { [key: string]: string };
    };
    test: {
        name: string;
        duration: number;
        totalMarks: number;
        negativeMarks?: number;
        questions?: any[];
    };
    questions?: any[];
}

export const PerformanceAnalytics: React.FC<PerformanceAnalyticsProps> = ({ results, test, questions: directQuestions = [] }) => {
    // Attempt to extract questions from all potential sources and ensure it's an array
    const rawQuestions = directQuestions.length > 0 
        ? directQuestions 
        : (test.questions || test.Questions || (results as any).questions || []);
    
    const questions = Array.isArray(rawQuestions) ? rawQuestions : [];

    const totalQuestions = results.correct + results.incorrect + results.unattempted;
    const attemptedQuestions = results.correct + results.incorrect;
    const attemptRate = totalQuestions > 0 ? (attemptedQuestions / totalQuestions) * 100 : 0;
    const timeUtilization = test.duration > 0 ? (results.time_taken / (test.duration * 60)) * 100 : 0;
    const scorePercentage = test.totalMarks > 0 ? (results.score / test.totalMarks) * 100 : 0;

    // Subject-wise analysis if questions have subject information
    const subjectAnalysis = questions.length > 0 ? calculateAnalytics(questions, results) : null;

    const { subjects: subjectAnalysisList, struggleZones } = subjectAnalysis || { subjects: [], struggleZones: [] };

    const getPerformanceInsights = () => {
        const insights = [];

        if (results.accuracy >= 90) {
            insights.push({ type: 'success', message: 'Excellent accuracy! You have a strong grasp of the concepts.' });
        } else if (results.accuracy >= 75) {
            insights.push({ type: 'good', message: 'Good performance! Focus on areas where you made mistakes.' });
        } else if (results.accuracy >= 60) {
            insights.push({ type: 'average', message: 'Average performance. Consider reviewing fundamental concepts.' });
        } else {
            insights.push({ type: 'improvement', message: 'There\'s room for improvement. Focus on understanding core concepts.' });
        }

        if (attemptRate < 80) {
            insights.push({ type: 'warning', message: 'You left many questions unattempted. Work on time management.' });
        }

        if (timeUtilization < 50) {
            insights.push({ type: 'info', message: 'You finished early. Consider reviewing your answers before submitting.' });
        } else if (timeUtilization > 95) {
            insights.push({ type: 'warning', message: 'You used almost all available time. Practice to improve speed.' });
        }

        return insights;
    };

    const insights = getPerformanceInsights();

    const getInsightColor = (type: string) => {
        switch (type) {
            case 'success': return 'bg-green-100 text-green-800 border-green-200';
            case 'good': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'average': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'improvement': return 'bg-red-100 text-red-800 border-red-200';
            case 'warning': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'info': return 'bg-gray-100 text-gray-800 border-gray-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="space-y-6">
            {/* Key Metrics */}
            <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Performance Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-brand-bg rounded-lg">
                        <div className="text-2xl font-bold text-brand-primary">{results.accuracy}%</div>
                        <div className="text-sm text-brand-text-secondary">Accuracy</div>
                    </div>
                    <div className="text-center p-4 bg-brand-bg rounded-lg">
                        <div className="text-2xl font-bold text-brand-primary">{attemptRate.toFixed(1)}%</div>
                        <div className="text-sm text-brand-text-secondary">Attempt Rate</div>
                    </div>
                    <div className="text-center p-4 bg-brand-bg rounded-lg">
                        <div className="text-2xl font-bold text-brand-primary">{scorePercentage.toFixed(1)}%</div>
                        <div className="text-sm text-brand-text-secondary">Score %</div>
                    </div>
                    <div className="text-center p-4 bg-brand-bg rounded-lg">
                        <div className="text-2xl font-bold text-brand-primary">{timeUtilization.toFixed(1)}%</div>
                        <div className="text-sm text-brand-text-secondary">Time Used</div>
                    </div>
                </div>
            </div>

            {/* Performance Insights */}
            <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Performance Insights</h3>
                <div className="space-y-3">
                    {insights.map((insight, index) => (
                        <div key={index} className={`p-3 rounded-lg border ${getInsightColor(insight.type)}`}>
                            <p className="text-sm font-medium">{insight.message}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pace Comparison Map */}
            {results.questionTimes && (
                <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-brand-text-primary">Speed Analysis (Pace Map)</h3>
                        <div className="flex space-x-4 text-xs">
                            <div className="flex items-center"><div className="w-2 h-2 bg-brand-primary rounded mr-1"></div>Your Time</div>
                            <div className="flex items-center"><div className="w-2 h-2 bg-zinc-300 rounded mr-1"></div>Global Avg</div>
                        </div>
                    </div>
                    <div className="space-y-4 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                        {questions.map((q, index) => {
                            const userTime = results.questionTimes?.[index] || 0;
                            const communalStats = typeof q.avg_time === 'string' ? JSON.parse(q.avg_time) : (q.avg_time || { avg: 0 });
                            const communalAvg = communalStats.avg || 45; // Default 45s if no data
                            const maxVal = Math.max(userTime, communalAvg, 120);
                            
                            return (
                                <div key={index} className="space-y-1">
                                    <div className="flex justify-between text-xs text-brand-text-secondary">
                                        <span>Q{index + 1}: {q.topic || 'General'}</span>
                                        <span>{userTime}s vs {communalAvg}s</span>
                                    </div>
                                    <div className="relative h-4 bg-brand-bg rounded-full overflow-hidden">
                                        {/* Global Avg Bar */}
                                        <div 
                                            className="absolute top-0 left-0 h-full bg-zinc-200 dark:bg-zinc-700 opacity-50"
                                            style={{ width: `${(communalAvg / maxVal) * 100}%` }}
                                        ></div>
                                        {/* User Time Bar */}
                                        <div 
                                            className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                                                userTime > communalAvg * 1.5 ? 'bg-red-500' : 
                                                userTime > communalAvg ? 'bg-amber-500' : 'bg-brand-primary'
                                            }`}
                                            style={{ width: `${(userTime / maxVal) * 100}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Struggle Zones & Subject Strength */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Subject Strength (Radar-style List) */}
                <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Subject Proficiency</h3>
                    <div className="space-y-4">
                        {subjectAnalysisList.length > 0 ? (
                            subjectAnalysisList.map((subject, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <span className="font-medium text-brand-text-primary">{subject.subject}</span>
                                        <span className="text-xs text-brand-text-secondary">{subject.accuracy.toFixed(0)}% Accuracy</span>
                                    </div>
                                    <div className="h-2 w-full bg-brand-bg rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full rounded-full ${
                                                subject.accuracy >= 80 ? 'bg-green-500' :
                                                subject.accuracy >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                            }`}
                                            style={{ width: `${subject.accuracy}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between text-[10px] text-brand-text-secondary uppercase tracking-wider">
                                        <span>Attempt: {subject.attemptRate.toFixed(0)}%</span>
                                        <span>Avg Speed: {subject.avgTime.toFixed(1)}s/q</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-brand-text-secondary">
                                No subject data available for this test.
                            </div>
                        )}
                    </div>
                </div>

                {/* Struggle Zones (Most Errors) */}
                <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 text-brand-text-primary text-red-500">Challenging Topics</h3>
                    {struggleZones && struggleZones.length > 0 ? (
                        <div className="space-y-3">
                            {struggleZones.map((zone, index) => (
                                <div key={index} className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 rounded-lg">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="font-semibold text-sm text-red-700 dark:text-red-400">{zone.name}</span>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white dark:bg-zinc-800 border border-red-200 dark:border-red-800 text-red-600">
                                            {zone.accuracy < 50 ? 'Critical' : 'Needs Focus'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-red-600/80 dark:text-red-400/60 mb-2">{zone.subject}</p>
                                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                                        <div className="text-brand-text-secondary">Accuracy: <span className="text-red-600 font-bold">{zone.accuracy.toFixed(0)}%</span></div>
                                        <div className="text-brand-text-secondary">Pace: <span className={zone.userAvgTime > zone.communalAvg ? 'text-amber-600 font-bold' : 'text-green-600 font-bold'}>
                                            {zone.userAvgTime.toFixed(0)}s (Avg {zone.communalAvg.toFixed(0)}s)
                                        </span></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-48 text-center text-brand-text-secondary">
                             <CheckCircleIcon className="w-12 h-12 text-green-500 mb-2 opacity-50" />
                             <p className="text-sm">No critical struggle zones identified. Great job!</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Original Summary List */}
            {subjectAnalysisList && subjectAnalysisList.length > 0 && (
                <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Subject-wise Performance</h3>
                    <div className="space-y-3">
                        {subjectAnalysisList.map((subject, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-brand-bg rounded-lg">
                                <div>
                                    <div className="font-medium text-brand-text-primary">{subject.subject}</div>
                                    <div className="text-sm text-brand-text-secondary">
                                        {subject.correct}/{subject.total} correct, {subject.avgTime.toFixed(1)}s avg speed
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`text-lg font-bold ${
                                        subject.accuracy >= 80 ? 'text-green-600' :
                                        subject.accuracy >= 60 ? 'text-yellow-600' : 'text-red-600'
                                    }`}>
                                        {subject.accuracy.toFixed(1)}%
                                    </div>
                                    <div className="text-xs text-brand-text-secondary">Accuracy</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recommendations */}
            <div className="bg-brand-surface border border-brand-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Recommendations</h3>
                <div className="space-y-3 text-sm text-brand-text-secondary">
                    <div className="flex items-start space-x-3">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <p>Review the detailed analysis to understand your mistakes and learn from them.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                        <BookmarkIcon className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                        <p>Practice more questions in areas where your accuracy is below 70%.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                        <ClockIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <p>Work on time management if you couldn't attempt all questions or finished too early.</p>
                    </div>
                    <div className="flex items-start space-x-3">
                        <XIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <p>Focus on understanding concepts rather than memorizing to reduce incorrect answers.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};